/**
 * useGenerateSuggestions
 *
 * Reusable hook for on-demand AI suggestion generation (Drafts / Insights tabs).
 * Calls the generate endpoint, connects to PubSub for real-time progress,
 * and exposes progress state for in-tab rendering.
 *
 * Follows the same pattern as useGenerateMore but tailored for suggestion generation:
 * - PubSub group: generate_suggestions_{correlationId}
 * - Events: step_started, step_progress, generate_suggestions_complete, generate_suggestions_failed
 *
 * **SessionStorage persistence**: active operations are persisted so that when
 * a user switches tabs (unmounts the component) and comes back, the hook
 * can reconnect to the PubSub group and resume showing progress.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppDispatch } from '../store';
import { showFeedbackModal } from '../store/actions/UserFeedbackActions';
import { getMultiHubSocketService } from '../utils/MultiHubSocketService';
import { useTranslation } from 'react-i18next';
import apis from '../utils/apis';

const HUB = 'ai_processing';
const MAX_AGE_MS = 120_000; // 2 minutes fallback timeout

export type SuggestionType = 'drafts' | 'insights';

interface UseGenerateSuggestionsOptions {
  matterUuid: string;
  suggestionType: SuggestionType;
  onComplete: () => void;
}

export interface GenerateSuggestionsProgress {
  message: string;
  percent: number;
  status: 'idle' | 'running' | 'complete' | 'failed';
}

/* ── SessionStorage helpers ────────────────────────────────── */

interface PersistedSuggestionOp {
  correlationId: string;
  group: string;
  startedAt: number;
  matterUuid: string;
  suggestionType: SuggestionType;
}

function storageKey(matterUuid: string, type: SuggestionType) {
  return `generate_suggestions_active_${matterUuid}_${type}`;
}

function persistOperation(op: PersistedSuggestionOp) {
  try {
    sessionStorage.setItem(storageKey(op.matterUuid, op.suggestionType), JSON.stringify(op));
  } catch { /* quota exceeded — non-critical */ }
}

function loadPersistedOperation(matterUuid: string, type: SuggestionType): PersistedSuggestionOp | null {
  try {
    const raw = sessionStorage.getItem(storageKey(matterUuid, type));
    if (!raw) return null;
    const op: PersistedSuggestionOp = JSON.parse(raw);
    if (Date.now() - op.startedAt > MAX_AGE_MS) {
      sessionStorage.removeItem(storageKey(matterUuid, type));
      return null;
    }
    return op;
  } catch {
    return null;
  }
}

function clearPersistedOperation(matterUuid: string, type: SuggestionType) {
  try {
    sessionStorage.removeItem(storageKey(matterUuid, type));
  } catch { /* ignore */ }
}

/* ── Hook ──────────────────────────────────────────────────── */

export function useGenerateSuggestions({ matterUuid, suggestionType, onComplete }: UseGenerateSuggestionsOptions) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState<GenerateSuggestionsProgress>({
    message: '',
    percent: 0,
    status: 'idle',
  });

  const groupRef = useRef<string | null>(null);
  const handlerRef = useRef<((raw: any) => void) | null>(null);
  const mountedRef = useRef(true);
  const generatingRef = useRef(false);
  const onCompleteRef = useRef(onComplete);
  const fallbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  /* ── PubSub cleanup (leave group + remove handler) ── */
  const cleanup = useCallback(() => {
    const multiHubService = getMultiHubSocketService();
    if (groupRef.current) {
      multiHubService.emit(HUB, 'leave', { group: groupRef.current });
      groupRef.current = null;
    }
    if (handlerRef.current) {
      multiHubService.off(HUB, '*', handlerRef.current);
      handlerRef.current = null;
    }
    if (fallbackTimerRef.current) {
      clearTimeout(fallbackTimerRef.current);
      fallbackTimerRef.current = null;
    }
  }, []);

  /* ── Finish helpers ── */
  const finishSuccess = useCallback(() => {
    clearPersistedOperation(matterUuid, suggestionType);
    cleanup();
    generatingRef.current = false;
    setIsGenerating(false);
    // Show "complete" briefly so the user sees the result
    setProgress(prev => ({ ...prev, status: 'complete', percent: 100 }));
    onCompleteRef.current();
    setTimeout(() => {
      if (mountedRef.current) {
        setProgress({ message: '', percent: 0, status: 'idle' });
      }
    }, 2000);
  }, [matterUuid, suggestionType, cleanup]);

  const finishFailure = useCallback(() => {
    clearPersistedOperation(matterUuid, suggestionType);
    cleanup();
    generatingRef.current = false;
    setIsGenerating(false);
    // Show "failed" briefly so the user sees the error
    setProgress(prev => ({ ...prev, status: 'failed' }));
    setTimeout(() => {
      if (mountedRef.current) {
        setProgress({ message: '', percent: 0, status: 'idle' });
      }
    }, 3000);
  }, [matterUuid, suggestionType, cleanup]);

  /* ── PubSub message handler ── */
  const handlePubSubMessage = useCallback((raw: any) => {
    if (!mountedRef.current) return;

    const event = Array.isArray(raw) ? raw[0] : raw || {};
    const innerData = event.data || {};
    const data = { ...event, ...innerData };

    const subType = data.sub_type || data.event_name || '';
    const message = data.message || '';
    const progressPercent = data.progress_percent || 0;

    // ── Step started ──
    if (subType === 'step_started') {
      setProgress({
        message: message || t('Starting...'),
        percent: progressPercent || 10,
        status: 'running',
      });
      return;
    }

    // ── Step progress ──
    if (subType === 'step_progress') {
      setProgress(prev => ({
        ...prev,
        message: message || prev.message,
        percent: progressPercent || prev.percent,
        status: 'running',
      }));
      return;
    }

    // ── Completion ──
    if (subType === 'generate_suggestions_complete') {
      const resultCount = data.result_count ?? 0;

      setProgress({
        message: t('Found {{count}} suggestions', { count: resultCount }),
        percent: 100,
        status: 'complete',
      });

      dispatch(showFeedbackModal({
        showModal: true,
        message: t('AI suggestions generated successfully'),
        modalType: 'success',
        duration: 3000,
      }));

      // Short delay so user sees the 100% state, then clean up and refresh
      setTimeout(() => {
        if (!mountedRef.current) return;
        finishSuccess();
      }, 1500);

      return;
    }

    // ── Failure ──
    if (subType === 'generate_suggestions_failed') {
      const errorMsg = data.error || data.message || t('Suggestion generation failed');

      setProgress({
        message: errorMsg,
        percent: 0,
        status: 'failed',
      });

      dispatch(showFeedbackModal({
        showModal: true,
        message: errorMsg,
        modalType: 'error',
        duration: 5000,
      }));

      setTimeout(() => {
        if (!mountedRef.current) return;
        finishFailure();
      }, 3000);

      return;
    }
  }, [dispatch, t, finishSuccess, finishFailure]);

  useEffect(() => {
    handlerRef.current = handlePubSubMessage;
  }, [handlePubSubMessage]);

  /* ── Connect to PubSub group and start listening ── */
  const connectToPubSub = useCallback(async (group: string) => {
    const multiHubService = getMultiHubSocketService();
    try {
      await multiHubService.connectToHub(HUB);
      multiHubService.emit(HUB, 'join', { group });
      multiHubService.on(HUB, '*', handlePubSubMessage);
      groupRef.current = group;
    } catch {
      setProgress({
        message: t('Processing (live updates unavailable)...'),
        percent: 50,
        status: 'running',
      });
    }
  }, [handlePubSubMessage, t]);

  /* ── Start the fallback timer from a given start time ── */
  const startFallbackTimer = useCallback((startedAt: number) => {
    if (fallbackTimerRef.current) clearTimeout(fallbackTimerRef.current);
    const elapsed = Date.now() - startedAt;
    const remaining = Math.max(0, MAX_AGE_MS - elapsed);
    fallbackTimerRef.current = setTimeout(() => {
      if (!mountedRef.current || !generatingRef.current) return;
      finishSuccess(); // Treat as silent success — refresh data
    }, remaining);
  }, [finishSuccess]);

  /* ── On mount: check for a persisted operation and reconnect ── */
  useEffect(() => {
    mountedRef.current = true;

    const persisted = loadPersistedOperation(matterUuid, suggestionType);
    if (persisted) {
      generatingRef.current = true;
      setIsGenerating(true);

      const elapsed = Date.now() - persisted.startedAt;
      const estimatedPercent = Math.min(90, Math.round((elapsed / MAX_AGE_MS) * 100));

      setProgress({
        message: t('Resuming progress...'),
        percent: estimatedPercent,
        status: 'running',
      });

      connectToPubSub(persisted.group);
      startFallbackTimer(persisted.startedAt);
    }

    return () => {
      mountedRef.current = false;
      const multiHubService = getMultiHubSocketService();
      if (groupRef.current) {
        multiHubService.emit(HUB, 'leave', { group: groupRef.current });
        groupRef.current = null;
      }
      if (handlerRef.current) {
        multiHubService.off(HUB, '*', handlerRef.current);
        handlerRef.current = null;
      }
      if (fallbackTimerRef.current) {
        clearTimeout(fallbackTimerRef.current);
        fallbackTimerRef.current = null;
      }
    };
  }, [matterUuid, suggestionType]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Trigger a new suggestion generation ── */
  const handleGenerate = useCallback(async () => {
    if (generatingRef.current || !matterUuid) return;

    generatingRef.current = true;
    setIsGenerating(true);
    setProgress({
      message: t('Starting request...'),
      percent: 5,
      status: 'running',
    });

    try {
      const endpoint = suggestionType === 'drafts'
        ? `/drafts/suggested/${matterUuid}/generate`
        : `/insights/suggested/${matterUuid}/generate`;

      const response = await apis.post(endpoint);
      const data = response.data;

      if (!data || !data.correlationId) {
        generatingRef.current = false;
        setIsGenerating(false);
        setProgress({ message: '', percent: 0, status: 'idle' });
        dispatch(showFeedbackModal({
          showModal: true,
          message: t('Failed to start suggestion generation'),
          modalType: 'error',
          duration: 3000,
        }));
        return;
      }

      const correlationId = data.correlationId;
      const group = data.pubsub?.group || `generate_suggestions_${correlationId}`;
      const now = Date.now();

      persistOperation({
        correlationId,
        group,
        startedAt: now,
        matterUuid,
        suggestionType,
      });

      await connectToPubSub(group);
      startFallbackTimer(now);
    } catch {
      generatingRef.current = false;
      setIsGenerating(false);
      setProgress({ message: '', percent: 0, status: 'idle' });
      clearPersistedOperation(matterUuid, suggestionType);
      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Failed to start suggestion generation'),
        modalType: 'error',
        duration: 3000,
      }));
    }
  }, [matterUuid, suggestionType, dispatch, connectToPubSub, startFallbackTimer, t]);

  return {
    isGenerating,
    progress,
    handleGenerate,
  };
}
